import { IonCard, IonCardContent, IonIcon, IonImg, IonSpinner, IonText } from '@ionic/react';
import { ellipsisVertical } from 'ionicons/icons';
import { useState } from 'react';
import { useHistory } from 'react-router';

import { ActionSheetReportCard } from '@/components/Actionsheets';
import { Icon } from '@/components/Icon';
import { IconRotate } from '@/components/Icon/types';
import { CustomLoading } from '@/components/Loading/CustomLoading';
import { Tag } from '@/components/Tag/Tag';
import { Toast } from '@/components/Toasts/Toast';

import {
  getTitleAndDescriptionFromReportData,
  getTitleAndDescriptionFromReportInfo,
} from '@/utils/getTitleAndDescriptionFromReportData';
import { mergeClassNames } from '@/utils/mergeClassNames';

import { useReportContext } from '@/contexts/reportContext';

import Running from '@/assets/icons/Running.png';
import SLSquatter from '@/assets/icons/SingleLegSquat.png';
import Squatter from '@/assets/icons/Squat.png';
import Walking from '@/assets/icons/Walking.png';
import ReportCardPattern from '@/assets/report-card-pattern.svg';

import { ReportInfo, ReportStatus, ReportType } from '@/models';

import styles from './ReportListCard.module.css';

interface ReportListCardProps {
  id: string;
  name: string;
  date: Date;
  reportType: ReportType;
  majorVersion: number;
  tags: string[];
  reportInfo: ReportInfo;
}

export const ReportListCard = ({ id, name, date, reportType, majorVersion, tags, reportInfo }: ReportListCardProps) => {
  const { title, description } = getTitleAndDescriptionFromReportInfo(reportInfo);
  const history = useHistory();
  const pending = reportInfo.status === ReportStatus.PENDING;
  const error =
    reportInfo.status === ReportStatus.ERROR ||
    reportInfo.reportType === ReportType.ERROR ||
    reportInfo.reportType === ReportType.KNEEL ||
    reportInfo.reportType === ReportType.KNEER;

  const { doDelete } = useReportContext();
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showErrorDoDelete, setShowErrorDoDelete] = useState(false);
  const [PDFError, setPDFError] = useState(false);

  const imageMap = new Map([
    [ReportType.WALK, Walking],
    [ReportType.RUN, Running],
    [ReportType.SQUAT, Squatter],
    [ReportType.SLSQUAT, SLSquatter],
  ]);

  const activityNamesMap = new Map([
    [ReportType.WALK, 'Walk'],
    [ReportType.RUN, 'Run'],
    [ReportType.SQUAT, 'Squat'],
    [ReportType.SLSQUAT, 'Single Leg Squat'],
  ]);

  const routeSegmentMap = new Map([
    [ReportType.WALK, 'Walk'],
    [ReportType.RUN, 'Run'],
    [ReportType.SQUAT, 'Squat'],
    [ReportType.SLSQUAT, 'SingleLegSquat'],
  ]);

  const handleClick = () => {
    if (routeSegmentMap.has(reportType)) {
      history.push(`/${majorVersion === 1 ? 'reportsV1' : 'reportsV2'}/${routeSegmentMap.get(reportType)}/${id}`, {
        title,
        description,
      });
    }
  };

  return (
    <>
      <ActionSheetReportCard
        doDelete={doDelete}
        isOpen={showActionSheet}
        onClose={() => setShowActionSheet(false)}
        reportInfo={reportInfo}
        showDeleteError={setShowErrorDoDelete}
        showPDFError={setShowErrorDoDelete}
        // onGeneratePDF={handleGeneratePDF}
      />

      <IonCard
        className={mergeClassNames(styles.card, error && styles.error)}
        onClick={!error && !pending ? handleClick : undefined}>
        {pending ? (
          <div className={styles.pending}>
            <CustomLoading message={'Creating report...'} size='small' />
          </div>
        ) : (
          <>
            <div className={styles.color}>
              <IonImg className={styles.icon} src={imageMap.get(reportType) ?? Walking} />

              <IonImg className={styles.pattern} src={ReportCardPattern} />
            </div>

            <IonCardContent className={styles.content}>
              <div className={pending ? styles.header_pending : styles.header}>
                <>
                  <IonText className={styles.name}>{activityNamesMap.get(reportType)}</IonText>
                  <IonText class={styles.date}>
                    <span className={styles.dateString}>
                      {date.toLocaleDateString(undefined, {
                        month: 'short',
                        year: 'numeric',
                        day: 'numeric',
                      })}
                    </span>
                    {date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                  </IonText>
                  <IonIcon
                    className={styles.chevron}
                    icon={ellipsisVertical}
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowActionSheet(true);
                    }}
                  />
                </>
              </div>

              <div className={styles.bottom}>
                <IonText className={styles.activity}>{name}</IonText>
                <Icon className={styles.chevron} color='#829696' direction={IconRotate.East} name='chevron' />
              </div>

              <div className={styles.tags}>
                {tags[0] != '' && (
                  <>
                    {tags.map((tag) => (
                      <Tag key={tag} size='sm' variant='blue'>
                        {' '}
                        {tag}{' '}
                      </Tag>
                    ))}
                  </>
                )}
              </div>

              {error && (
                <div className={styles.errorMessage}>
                  <IonText className={styles.errorText}>
                    {reportInfo.status === ReportStatus.ERROR &&
                      `Sorry, we couldn't process your data. Please contact support.`}
                    {reportInfo.reportType === ReportType.ERROR &&
                      `Sorry, we couldn't process your data. Please contact support.`}
                    {reportInfo.reportType === ReportType.KNEEL && `Left Thigh and Calf Sensors seem to be swapped.`}
                    {reportInfo.reportType === ReportType.KNEER && `Right Thigh and Calf Sensors seem to be swapped.`}
                  </IonText>
                </div>
              )}
            </IonCardContent>
          </>
        )}
      </IonCard>

      <Toast
        header='Error deleting file'
        isOpen={showErrorDoDelete}
        message={`No report ID for the given report ${reportInfo.filename}.`}
        onDidDismiss={() => setShowErrorDoDelete(false)}
        type='warning'
      />
      <Toast
        header='Error downloading file'
        isOpen={PDFError}
        message={`Can't download PDF for ${reportInfo.filename}, please contact support.`}
        onDidDismiss={() => setPDFError(false)}
        type='warning'
      />
    </>
  );
};
